/* eslint-disable */
// @ts-nocheck
'use client';

import React, { useEffect, ChangeEvent, useState, useCallback } from 'react';
import Input from '@/components/atoms/project-builder/input';
import Typography from '@/components/atoms/project-builder/typography';
import { useParams, usePathname } from 'next/navigation';
import Dropdown from '@/components/atoms/project-builder/dropdown';
import RoomDetails from './roomsDetails';
import Button from '@/components/atoms/project-builder/button';
import { projectType, PHONE_PATTERN, EMAIL_PATTERN, projectLocation, projectStatus } from '../data';
import { sdk } from 'sdk';
import useAccount from '@/lib/hooks/useAccount';
import useProjectBuilder from '@/lib/hooks/useProjectBuilder';
import Spinner from '@/components/organisms/spinner';
import { PROJECT_CREATED, PROJECT_UPDATED } from '@/helpers/constants/events';
import { ctDataToFormData } from '../data';
import SaveConfirmationPopup from '../save-confirmation-popup';
import ProductUpload from '../productUpload';
import { Tooltip } from 'react-tooltip';
import {
    TextInputField,
    DropdownField,
    SpecifierSection,
    CustomerSection,
    ProjectMetaSection,
    RoomsSection
} from './projectForms';

type ProjectDetailsProps = {
    activeTab: number;
    handleTabClick: (tabIndex: number, formData: any) => void;
    projectFormData: any;
    setIsFormDirty: any;
    isFormDirty: boolean;
    openSaveConfirmation: boolean;
    setOpenSaveConfirmation: any;
    handleCancelLeave: any;
    handleConfirmLeave: any;
    tabIndexValue: number;
    setActiveTab: (tabIndex: number) => void;
};

type ChangeHandler = (event: ChangeEvent<HTMLInputElement>) => void;

const ProjectDetails: React.FC<ProjectDetailsProps> = ({ activeTab, tabIndexValue, setActiveTab, setIsFormDirty, isFormDirty, handleCancelLeave,
    handleConfirmLeave, openSaveConfirmation, setOpenSaveConfirmation, handleTabClick, projectFormData }) => {
    const { createShoppingList, updateProjectStatus } = useProjectBuilder();
    const { locale } = useParams();
    const [loading, setLoading] = useState<any>(false);
    const pathname = usePathname();
    const { account } = useAccount();
    const isEditPage = pathname.indexOf('/edit') !== -1 || pathname.indexOf('/view') !== -1;

    let requiredFields: any = [
        { name: 'projectName', message: 'Project name is required' },
        { name: 'projectType', message: 'Project type is required' },
        { name: 'projectLocation', message: 'Project location is required' },
        { name: 'username', message: 'Specifier Username is required' },
        { name: 'selectedRoomDetails', message: 'Minimum one room is required.' }
    ];

    const [formErrors, setFormErrors] = useState<any>({});
    const [selectedRoomErrors, setSelectedRoomErrors] = useState<any>(false);
    const [isSubmitted, setIsSubmitted] = useState<any>(false);
    const [formData, setFormData] = useState<any>({});
    const [selectedRooms, setSelectedRooms] = useState([]);
    const [selectedRoomsFromParent, setSelectedRoomsFromParent] = useState([]);
    const [formEdit, setFormEdit] = useState(false);
    const [disableRoom, setDisableRoom] = useState(false);
    const [createProjectError, setCreateProjectError] = useState('');
    const [filteredProjectLocation, setFilteredProjectLocation] = useState([]);
    const [editFormData, setEditFormData] = useState<any>({});
    const [navigationToDiffStepperid, setNavigationToDiffStepperid] = useState(0);


    useEffect(() => {
        setFormEdit(false);
        if (projectFormData) {
            setDisableRoom(true);
            const projectData = {
                projectName: projectFormData.projectName || '',
                username: projectFormData.username || '',
                projectType: projectFormData.projectType || '',
                projectStatus: projectFormData.projectStatus || '',
                projectLocation: projectFormData.projectLocation || '',
                opportunityNumber: projectFormData.opportunityNumber || '',
                projectVersion: projectFormData.projectVersion || 'Version 1',
                customerName: projectFormData.customerName || '',
                customerAddress: projectFormData.customerAddress || '',
                customerEmail: projectFormData.customerEmail || '',
                customerPhone: projectFormData.customerPhone || '',
            };
            setFormData(projectData);
            if (isEditPage) {
                projectData.shoppingListId = projectFormData.shoppingListId;
                projectData.version = projectFormData.version;
                setEditFormData(projectData);
            }
            setSelectedRoomsFromParent(projectFormData.selectedRoomDetails);
            if (!isEditPage) {
                setFormEdit(true);
            }
        }
    }, [projectFormData]);

    useEffect(() => {
        const filteredLocation = projectLocation.filter((item) => item.region === '*' || item.region === locale);
        setFilteredProjectLocation(filteredLocation);
    }, [locale]);

    useEffect(() => {
        if (!isEditPage && (account?.firstName || account?.lastName)) {
            setFormData((prevData: any) => ({
                ...prevData,
                username: prevData?.username ?? `${account?.firstName || ''} ${account?.lastName || ''}`.trim(),
            }));
        }
    }, [account, isEditPage]);

    const addMissingRooms = (data) => {
        for (let room of data) {
            const { count, value } = room;
            if (count > value.length) {
                for (let i = 1; i <= count; i++) {
                    if (!value.includes(i)) {
                        value.push(i);
                    }
                    if (value.length === count) break;
                }
                value.sort((a, b) => a - b);
            } else if (count < value.length) {
                room.value = value.slice(0, count);
            }
        }
        return data;
    };

    const handleSelectedRoomsChange = (updatedRooms) => {

        setSelectedRooms(addMissingRooms(updatedRooms));
        if (updatedRooms.length) {
            setSelectedRoomErrors(false);
        }
    };

    const handleChange = useCallback((name: string, value: string) => {
        setFormData((prevData: any) => ({ ...prevData, [name]: value }));
    }, []);

    const handleInputChange: ChangeHandler = (event) => {
        setIsFormDirty(true);
        const { name, value } = event.target;
        const restrictedFields = ['projectName', 'username', 'opportunityNumber', 'customerName'];
        const invalidCharsRegex = /[!@#$%^&*()_+={}\[\];:'"\\|,.<>?~`/]/g;
        const addressAllowedCharsRegex = /[^a-zA-Z0-9\s.,\/\-\'#]/g;
        let sanitizedValue = value;

        if (restrictedFields.includes(name)) {
            sanitizedValue = value.replace(invalidCharsRegex, '');
        } else if (name === 'customerAddress') {
            sanitizedValue = value.replace(addressAllowedCharsRegex, '');
        }

        handleChange(name, sanitizedValue);
        if (isSubmitted) {
            const newErrors = handleValidation({ ...formData, [name]: sanitizedValue });
            if (newErrors[name] !== formErrors[name]) {
                setFormErrors(newErrors);
            }
        }
    };
    const handleDropdownChange: ChangeHandler = (name, option) => {
        setIsFormDirty(true);
        handleChange(name, option);
        if (isSubmitted) {
            const newErrors = handleValidation({ ...formData, [name]: option });

            if (newErrors[name] !== formErrors[name]) {
                setFormErrors(newErrors);
            }
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, field?: string) => {
        const restrictedFields = ['projectName', 'username', 'opportunityNumber', 'customerName'];
        const addressField = 'customerAddress';

        if (field && (restrictedFields.includes(field) || field === addressField)) {
            const invalidCharsRegex =
                field === addressField
                    ? /[^a-zA-Z0-9\s.,\/\-\'#]/
                    : /[!@#$%^&*()_+={}\[\];:'"\\|,.<>?~`/]/;

            if (invalidCharsRegex.test(event.key)) {
                event.preventDefault();
            }
        }

        if (event.key === 'Enter') {
            event.preventDefault();
            handleSubmit(event as any);
        }
    };
    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>, field: string) => {
        event.preventDefault();
        setIsFormDirty(true);

        const restrictedFields = ['projectName', 'username', 'opportunityNumber', 'customerName'];
        let pastedData = event.clipboardData.getData('text');

        if (field === 'customerAddress') {
            pastedData = pastedData.replace(/[^a-zA-Z0-9\s.,\/\-\'#]/g, '');
            pastedData = pastedData.substring(0, 200);
        } else if (restrictedFields.includes(field)) {
            pastedData = pastedData.replace(/[!@#$%^&*()_+={}\[\];:'"\\|,.<>?~`/]/g, '');
            pastedData = pastedData.substring(0, 50);
        }
        handleChange(field, pastedData);
    };

    const handleValidation = (values: any) => {
        let errors: any = { ...formErrors };
        Object.keys(values).forEach((key) => {
            const value = values[key];
            switch (key) {
                case 'customerEmail':
                    if (!value) {
                        delete errors[key]
                    }
                    if (value) {
                        if (!EMAIL_PATTERN.test(value)) {
                            errors[key] = 'Please enter a valid email address';
                        } else {
                            delete errors[key];
                        }
                    }
                    break;
                case 'customerPhone':
                    if (!value) {
                        delete errors[key];
                    } 
                    if (value) {
                        if (value.length < 8) {
                            errors[key] = 'Customer phone number must be minimum of 8 digits';
                        } else if (!PHONE_PATTERN.test(value)) {
                            errors[key] = 'Please enter a valid phone number';
                        } else {
                            delete errors[key];
                        }
                    }
                    break;
                case 'username':
                    if (!value) {
                        errors[key] = 'Specifier Username is required';
                    } else {
                        delete errors[key];
                    }
                    break;
                case 'projectName':
                    if (!value) {
                        errors[key] = 'Project name is required';
                    } else {
                        delete errors[key];
                    }
                    break;
                case 'projectType':
                    if (!value) {
                        errors[key] = 'Project type is required';
                    } else {
                        delete errors[key];
                    }
                    break;
                case 'projectLocation':
                    if (!value) {
                        errors[key] = 'Project location is required';
                    } else {
                        delete errors[key];
                    }
                    break;
                case 'selectedRoomDetails':
                    if (disableRoom) {
                        delete errors[key];
                        setSelectedRoomErrors(false);
                        break;
                    }
                    if (!selectedRooms.length) {
                        errors[key] = 'Minimum one room is required.';
                        setSelectedRoomErrors(true);
                    } else {
                        delete errors[key];
                        setSelectedRoomErrors(false);
                    }
                    break;
                default:
                    break;
            }
        });
        return errors;
    };

    const getChangedValues = (initialForm, updatedForm) => {
        let changes = {};
        for (const key in updatedForm) {
            if (typeof updatedForm[key] === 'object' && updatedForm[key] !== null) {
                if (JSON.stringify(updatedForm[key]) !== JSON.stringify(initialForm[key])) {
                    changes[key] = updatedForm[key].value;
                }
            } else {
                if (updatedForm[key] !== initialForm[key]) {
                    changes[key] = updatedForm[key];
                }
            }
        }
        return changes;
    };


    const getFormattedRoomNames = (selectedValue) => {
        return selectedValue.flatMap(room => {
            if (room.count === 1) {
                return [room.roomName];
            } else if (room.count > 1 && room.value.length > 0) {
                return room.value.map(value => `${room.roomName} ${value}`);
            } else {
                return [];
            }
        });
    };

    const addNumberIfMissing = (names: string[]) => {
        return names.map(name => {
            // If it ends in digits and there's no space before the number, insert one
            if (/\d+$/.test(name) && !/\s\d+$/.test(name)) {
                return name.replace(/(\d+)$/, ' $1');
            }
            // If it doesn't end in a number at all, append ' 1'
            if (!/\d+$/.test(name)) {
                return `${name} 1`;
            }
            return name;
        });
    };

    const handleSubmit = async (e: any, isPopupConfirmation?: boolean) => {
        setCreateProjectError('');
        e.preventDefault();
        setIsSubmitted(true);
        setSelectedRoomErrors(false);
        setIsFormDirty(false);
        setOpenSaveConfirmation(false);
        let errors: any = {};
        formData['selectedRoomDetails'] = selectedRooms;
        requiredFields.forEach((field) => {
            if (field.name === 'selectedRoomDetails' && disableRoom) return;
            const value = formData[field.name];
            const isEmptyString = typeof value === 'string' && !value.trim();
            const isEmptyArray = Array.isArray(value) && value.length === 0;
            const isEmptyObject = typeof value === 'object' && value !== null && !Array.isArray(value) && Object.keys(value).length === 0;

            if (
                value === undefined ||
                value === null ||
                isEmptyString ||
                isEmptyArray ||
                isEmptyObject
            ) {
                errors[field.name] = field.message;
            }
        });
        const validationErrors = handleValidation(formData);
        errors = { ...errors, ...validationErrors };
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            handleCancelLeave();
            setIsFormDirty(true);
            return;
        }
        try {
            if (!isEditPage) {
                setLoading(true);
                const payload = {
                    projectDetails: {
                        projectName: formData.projectName,
                        projectType: formData?.projectType?.value,
                        projectStatus: 'open',
                        projectLocation: formData.projectLocation?.value,
                        opportunityNumber: formData?.opportunityNumber,
                        projectVersion: "Version 1"
                    },
                    customerDetails: {
                        customerName: formData.customerName?.trim() || '',
                        customerAddress: formData.customerAddress?.trim() || '',
                        customerEmail: formData.customerEmail || '',
                        customerPhone: formData.customerPhone || '',
                    },
                    selectedRoomDetails: addNumberIfMissing(getFormattedRoomNames(selectedRooms)),
                    specifierDetails: {
                        username: formData.username,
                    }
                };
                const responseBody: any = await createShoppingList(payload);
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    event: PROJECT_CREATED,
                    userId: account?.accountId,
                });
                handleTabClick(isPopupConfirmation ? 1 : 2, responseBody.shoppingList);
                setLoading(false);
            } else {
                setLoading(true);
                const formChanges = getChangedValues(editFormData, formData);
                delete formChanges.selectedRoomDetails;
                const payload = {
                    shoppingListId: editFormData.shoppingListId,
                    version: editFormData.version,
                    items: {
                        ...formChanges,
                        ...(formChanges.projectStatus && { status: formChanges.projectStatus }),
                    },
                };
                let tab = 2;
                if (tabIndexValue !== 0 && activeTab !== tabIndexValue) {
                    tab = tabIndexValue;
                } else if (isPopupConfirmation) {
                    tab = 1;
                }
                if (Object.keys(payload.items).length) {
                    const responseBody: any = await updateProjectStatus(payload);
                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({
                        event: PROJECT_UPDATED,
                        shoppingListId: editFormData?.shoppingListId,
                        userId: account?.accountId,
                    });

                    handleTabClick(tab, ctDataToFormData(responseBody.shoppingList));
                } else {
                    handleTabClick(tab, projectFormData);
                }
                setLoading(false);
            }
            handleConfirmLeave();
        } catch (error) {
            setLoading(false);
            const errorMessage = error.message?.replace(/^Error:\s*/, '');
            if (errorMessage?.toLowerCase()?.includes('failed to fetch')) {
                setCreateProjectError('No internet connection. Please check your connection and try again.');
            } else if (errorMessage?.includes('The project name already exists')) {
                setCreateProjectError(error.message?.replace(/^Error:\s*/, ''));
                setTimeout(() => setCreateProjectError(''), 3000);
            } else {
                setCreateProjectError('Something went wrong. Please try again.');
            }
        }
    };
    const editRoomStyleDisable = !isEditPage && projectFormData ? { opacity: "0.5", cursor: "not-allowed" } : {};
    const discardConfirmation = () => {
        setIsFormDirty(false);
        setOpenSaveConfirmation(false);
        if (tabIndexValue !== 0 && activeTab !== tabIndexValue) {
            handleTabClick(tabIndexValue, projectFormData);
        }
        setTimeout(() => {
            handleConfirmLeave();
        }, 100);
    };

    const closeConfirmation = () => {
        setOpenSaveConfirmation(false);
        handleCancelLeave();
    };

    return (
        <>
            {loading && <Spinner />}
            <form onSubmit={handleSubmit}>
                <SpecifierSection
                    formData={formData}
                    formErrors={formErrors}
                    onChange={handleInputChange}
                    onPaste={handlePaste}
                    onKeyDown={handleKeyDown}
                />

                <CustomerSection
                    formData={formData}
                    formErrors={formErrors}
                    onChange={handleInputChange}
                    onPaste={handlePaste}
                    onKeyDown={handleKeyDown}
                />

                <ProjectMetaSection
                    formData={formData}
                    formErrors={formErrors}
                    onChange={handleInputChange}
                    onDropdownChange={handleDropdownChange}
                    options={{ projectType, projectLocation, projectStatus }}
                    isEdit={isEditPage}
                />

                <RoomsSection
                    selectedRoomsFromParent={selectedRoomsFromParent}
                    onSelectedRoomsChange={handleSelectedRoomsChange}
                    disabled={disableRoom}
                    showError={selectedRoomErrors}
                    setIsFormDirty={setIsFormDirty}
                />

                {isEditPage && (
                    <div className='mt-[30px] flex items-center gap-[20px]'>
                        <div><ProductUpload customerId={account?.accountId} projectId={projectFormData?.shoppingListId} projectForm={projectFormData} /></div>
                        <a
                            href='/images/files/example_spectool_upload_sku_list.csv'
                            className=' font-semibold text-[16px] text-[#000000] cursor-pointer'
                            download="example_spectool_upload_sku_list.csv"
                            data-tooltip-id="see-template-tooltip"
                            data-tooltip-content="Excel template for uploading your project"
                        >
                            See Template
                        </a>
                        <Tooltip id="see-template-tooltip" place="top" style={{ fontSize: '14px' }} />
                    </div>)}
                <div className="flex items-center">
                    <Button
                        className={`!mb-[32px] !mt-[40px] w-full !cursor-pointer rounded-none !bg-gray-700 !px-6 !py-[13px] text-center text-14 disabled:cursor-auto disabled:!text-white disabled:!bg-gray-400 md:!mt-[40px] md:w-fit`}
                        type="submit"
                        disabled={formEdit}
                    >
                        Save and continue
                    </Button>
                    <Typography className={`ml-2.5 mt-3 text-xs font-medium leading-4 text-red-500`} as="span">
                        {createProjectError}
                    </Typography>
                </div>
            </form>
            <div className="savepopup-container">
                <SaveConfirmationPopup
                    isOpen={openSaveConfirmation}
                    handleDiscard={discardConfirmation}
                    handleSaveNContinue={(e) => handleSubmit(e, true)}
                    handleClose={closeConfirmation}
                    disabledSaveNContinue={formEdit}
                />
            </div>
        </>
    );
};

export default ProjectDetails;